import {
  Box,
  Button,
  Container,
  FormControl,
  IconButton,
  InputAdornment,
  Modal,
  OutlinedInput,
  Paper,
  TextField,
} from "@mui/material";
import React, { Dispatch, SetStateAction, useState, useEffect } from "react";
import LoadingModal from "../../components/Modals/LoadingModal";
import Logo from "../../components/Logo";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import { useNavigate, useSearchParams } from "react-router-dom";
import { showError } from "../../utils/helpers";

const SetPassword = () => {
  const labels = useAppSelector(selectLabels);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);
  const [showConfirmedModal, setShowConfirmedModal] = useState<boolean>(false);

  useEffect(() => {
    if (!token) {
      console.error("No se recibió el token en la URL");
      showError(dispatch, labels.generic_error);
    }
  }, [token, dispatch, labels.generic_error]);

  const handleClickShowPassword = (
    setState: React.Dispatch<SetStateAction<boolean>>
  ) => setState((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!token) {
      showError(dispatch, "No se recibió un token válido.");
      return;
    }

    try {
      const response = await fetch(
        "https://strapi.passtrack.mx/api/custom-auth/reset-password",
        {
          method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: token,
          password,
          passwordConfirmation: confirmPassword,
        }),
      });

      if (response.ok) {
        setShowConfirmedModal(true);
        setTimeout(() => {
          setShowConfirmedModal(false);
          navigate("/login");
        }, 3000);
      } else {
        const error = await response.json();
        showError(dispatch, error.message || "Error al restablecer la contraseña.");
      }
    } catch (error) {
      console.error(error);
      showError(dispatch, "Error al restablecer la contraseña.");
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        paddingY: "10px",
        boxSizing: "border-box",
      }}
    >
      <LoadingModal open={isLoading} setOpen={setIsLoading} />
      <Modal 
        open={showConfirmedModal}
        onClose={() => setShowConfirmedModal(false)}
      >
      <Box
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          style={{
            maxWidth: 350,
            minWidth: 350,
            width: "40%",
            boxSizing: "border-box",
            paddingTop: 20,
            paddingBottom: 20,
            paddingRight: 30,
            paddingLeft: 30,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <p style={{ textAlign: "center" }}>
            Contraseña establecida exitosamente.
          </p>
        </Paper>
      </Box>
    </Modal>
    <Paper
        variant="outlined"
        className="w-100 p-10 flex flex-dir-col justify-content-center align-items-center border-box scroll"
      >
        <div
          className="w-100 flex flex-dir-col align-items-center justify-content-center gap-6"
          style={{
            paddingTop: 40,
            paddingBottom: 40,
          }}
        >
        <Logo type={"passTrackLogo"} alt="passtrack" className="logo" />
          <p
            className={`main-title mt-8 ${!token ? "mb-8" : ""}`
          }        
        >
        {labels.set_password}
          </p>
          {token && (
            <div className="mt-10 mb-10">
              <p className="font-14">
                Has confirmado el correo electrónico, establece tu contraseña para poder acceder a la aplicación.
              </p>
            </div>
          )}
          <form className="w-100" onSubmit={onSubmit}>
          <div className="w-100 mb-8"> 
              <p className="body-text-sm mb-3">{labels.password} *</p>
              <FormControl fullWidth variant="outlined">
                <OutlinedInput
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={labels.new_password}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => handleClickShowPassword(setShowPassword)}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </div>

            <div className="w-100 mb-8">
              <p className="body-text-sm mb-3">{labels.confirm_password} *</p>
              <FormControl fullWidth variant="outlined">
                <OutlinedInput
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder={labels.confirm_password}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => 
                          handleClickShowPassword(setShowConfirmPassword)
                        }
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
            </div>

            <Button
              fullWidth
              disableElevation
              size="large"
              variant="contained"
              disabled={!confirmPassword || !password || confirmPassword !== password}
              type="submit"
            >
              {labels.save_password}
            </Button>
          </form>
        </div>
      </Paper>
    </Container>
  );
};

export default SetPassword;
